<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, TransitionRoot } from '@headlessui/vue'

interface Option {
  [key: string]: any
}
const buttonRef = ref<HTMLElement | null>(null)

const props = withDefaults(
  defineProps<{
    modelValue: any
    options: Option[]
    optionLabel?: string // The key to display as label
    optionValue?: string // The key to use as value
    placeholder?: string
    disabled?: boolean
    buttonClass?: string
    optionsClass?: string
    closeOnSelect?: boolean
  }>(),
  {
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Select an option',
    disabled: false,
    buttonClass: '',
    optionsClass: '',
    closeOnSelect: true
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

// Computed properties to handle option labels and values
const optionLabel = computed(() => props.optionLabel || 'label')
const optionValue = computed(() => props.optionValue || 'value')

// Computed property to manage v-model binding
const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

// Compute the label to display on the button
const displayLabel = computed(() => {
  if (props.modelValue) {
    const selectedOption = props.options.find(option =>
      props.optionValue ? option[props.optionValue] === props.modelValue : option === props.modelValue
    );
    return selectedOption ? getOptionLabel(selectedOption) : props.placeholder || 'Select an option';
  }
  return props.placeholder || 'Select an option';
});

// For debugging purposes: display selected value as string
const internalValueDisplay = computed(() => {
  if (props.modelValue) {
    return typeof props.modelValue === 'string'
      ? props.modelValue
      : props.modelValue[optionLabel.value] || JSON.stringify(props.modelValue)
  }
  return 'None'
})

// Helper functions to get option label and key
const getOptionLabel = (option: Option) => {
  return typeof option === 'string' ? option : option[optionLabel.value] || optionLabel.value
}

const getOptionKey = (option: Option) => {
  return typeof option === 'string' ? option : option[optionValue.value] || option[optionLabel.value] || JSON.stringify(option)
}

const handleSelect = (value: any, open: boolean) => {
  if (props.closeOnSelect) {
    // Optional side-effects here, dropdown will close automatically
    nextTick(() => {
      if (buttonRef.value && buttonRef.value?.el) {
        buttonRef.value.el.click();
      }
    })
  }
}
</script>

<template>
  <Listbox v-model="internalValue" :disabled="disabled" v-slot="{ open, selected }">
    <div class="relative">
      <!-- Debugging: Display current modelValue -->
      <!-- <div class="mb-2 text-sm text-gray-500">Selected: {{ internalValueDisplay }}</div> -->

      <ListboxButton ref="buttonRef"
        class="mpd-listbox-button relative w-full cursor-default rounded-lg bg-white gpy-0.75 gpl-1.25 gpr-1.5 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm relative"
        :class="buttonClass">
        <span class="label">{{ displayLabel }}</span>
        <span class="pointer-events-none absolute right-0 top-50% translate-y--50% translate-x--100%">
          <i class="sec-dropdown-icon i-mdi:chevron-down flex w-5 h-5 text-gray-500" />
        </span>
      </ListboxButton>

      <!-- <TransitionRoot enter="transition ease-out duration-100" enter-from="transform opacity-0 scale-95"
        enter-to="transform opacity-100 scale-100" leave="transition ease-in duration-75"
        leave-from="transform opacity-100 scale-100" leave-to="transform opacity-0 scale-95"> -->
      <TransitionRoot :show="open" as="template" enter="transition ease-out duration-100"
        enter-from="transform opacity-0 scale-95" enter-to="transform opacity-100 scale-100"
        leave="transition ease-in duration-75" leave-from="transform opacity-100 scale-100"
        leave-to="transform opacity-0 scale-95">
        <ListboxOptions
          class="mpd-listbox-options mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm gpl-1"
          :class="optionsClass">

          <ListboxOption v-for="option in options" :key="getOptionKey(option)"
            :value="props.optionValue ? option[props.optionValue] : option" as="template" v-slot="{ active, selected }">
            <li class="mpd-listbox-option" :class="{
              'active': active,
              'selected': selected,
              'cursor-pointer select-none relative gpy-0.33 gpx-0': true,
            }">
              <span :class="{ 'font-semibold': selected, 'font-normal': !selected }" class="block truncate text-left">
                {{ getOptionLabel(option) }}
              </span>
              <span v-if="selected" class="absolute top-50% inset-y-0 left-0 flex items-start pl-3 -translate-y-1/2">
                <!-- <i class="sec-check-icon flex i-carbon:checkmark" /> -->
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </TransitionRoot>
    </div>
  </Listbox>
</template>


<style scoped lang="scss">
.mpd-listbox-button {
  /* Customize your button styles here */

  border-radius: gr(2);

  background: var(--i-blue);


  .label {
    color: var(--i-blue);
    font-weight: 500;
    font-size: fs(0.5);
    color: white;
  }
  .sec-dropdown-icon {
    transition: transform 0.2s ease-in-out;
    color: white;
    
  }

  &[data-headlessui-state="open"] {
    outline: 2px solid var(--i-blue) !important;

    .sec-dropdown-icon {
      transform: rotate(180deg);
    }
  }
}

.mpd-listbox-options {
  /* Customize your options list styles here */
  padding-bottom: gr(0.25);
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: white;
}

.mpd-listbox-option {
  /* Customize your option item styles here */

  margin-bottom: 0;


  &:hover {

    &:active {

    }
  }

  &.active {


    &:hover {


      &:active {

      }
    }
  }

  &.selected {

    span {
      color: var(--i-blue);
    }

    &:hover {

      &:active {

      }
    }
  }
}

/* Example styles, adjust as needed */
.sec-dropdown-icon {
  /* Style for the dropdown icon */
}

.sec-check-icon {
  /* Style for the checkmark icon */
}
</style>